import { gravatarUrl } from '@goaltree/ui/lib/gravatar'
import { cn } from '@goaltree/ui/lib/utils'
import { useMemo, useState } from 'react'

import { dicebearUrl } from '@/lib/avatar'

const SELF_KEY = 'gt_self_avatar'
function remember(src: string) {
  try {
    if (localStorage.getItem(SELF_KEY) !== src) localStorage.setItem(SELF_KEY, src)
  } catch {}
}
function recall() {
  try {
    return localStorage.getItem(SELF_KEY) ?? undefined
  } catch {
    return undefined
  }
}

/**
 * Avatar image with a fallback chain: real photo → Gravatar (when an email is
 * known) → DiceBear cartoon. Never falls back to initials. Gravatar is skipped
 * when no email is available (e.g. other users on the leaderboard).
 */
export function UserAvatar({
  image,
  email,
  name,
  size = 96,
  className,
  self = false,
}: {
  image?: string | null
  email?: string | null
  name?: string | null
  size?: number
  className?: string
  // The signed-in user's own avatar: remembers the last photo so it shows at once on
  // the next load instead of a placeholder while the profile query resolves.
  self?: boolean
}) {
  const loading = image === undefined && email === undefined && name === undefined
  if (self && image) remember(image)
  if (loading) image = self ? recall() : undefined
  const chain = useMemo(
    () =>
      [
        image || null,
        email ? gravatarUrl(email, size) : null,
        dicebearUrl(name || email || ''),
      ].filter(Boolean) as string[],
    [image, email, name, size],
  )
  const [level, setLevel] = useState(0)
  const src = chain[Math.min(level, chain.length - 1)]
  // Nothing known yet: a neutral disc rather than a stranger's cartoon.
  if (loading && !image) return <span aria-hidden className={cn('block bg-muted', className)} />
  return (
    <img
      src={src}
      alt={name ?? ''}
      referrerPolicy="no-referrer"
      loading="lazy"
      decoding="async"
      width={size}
      height={size}
      onError={() => setLevel((l) => (l < chain.length - 1 ? l + 1 : l))}
      className={className}
    />
  )
}
